import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TransactionsService } from './transactions.service';
import {
  PayoutColumns,
  PaymentColumns,
  AllTransactionsColumns,
  TransferColumns,
  StripeFeeColumns,
} from '../models/payment-column-config';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateConverterService } from '../../../helpers/date-converter.service';
import {
  Transaction,
  Column,
  IStripeTransactionRequest,
  IStripeTransactionResponse,
  IStripeTransactionResponseBody,
  IStripeFeeResponse,
  IStripeFeeResponseBody,
} from './transaction.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator } from '@angular/material/paginator';

interface TypeofPayments {
  value: string;
  viewValue: string;
  visible?: boolean;
  paymentVisible?: boolean;
  transferVisible?: boolean;
}
@Component({
  selector: 'app-stripe',
  templateUrl: './stripe.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './stripe.component.scss',
})
export class StripeComponent implements OnInit {
  transactions: Transaction[] = [];
  minDate: Date;
  maxDate: Date;
  startDate!: Date;
  endDate!: Date;

  totalResults: number = 0;
  pageSize: number = 50;
  pageOffset: number = 0; // Default page offset
  pageIndex: number = 0;

  currentSortColumn: string = 'Id'; // Default sort column
  currentSortDirection: string = 'asc'; // Default sort direction
  stripeDataForm!: FormGroup;

  paymentColumns = PaymentColumns;
  payoutsColumns = PayoutColumns;
  stripeFeeColumns = StripeFeeColumns;
  allTransactionColumns = AllTransactionsColumns;
  transferColumns = TransferColumns;

  selectedTab: string = 'payment'; // Default selected tab
  paymentType: string = 'payment'; //'All'; // Default payment type

  @ViewChild('stripeDatePicker') stripeDatePicker!: MatDateRangePicker<Date>;
  // @ViewChild('') paginator!: MatPaginator;
  // The filtered list to display in the table
  displayedTransactions: IStripeTransactionResponseBody[] = [];
  displayedColumns: Column[] = [];

  // selectedValue: string = 'All';
  isTypeOfPayment: boolean = true;
  typesOfPayments: TypeofPayments[] = [
    {
      value: 'All Transactions',
      viewValue: 'All',
      visible: true,
      transferVisible: true,
      paymentVisible: true,
    },
    {
      value: 'transfer_refund',
      viewValue: 'Transfer Refund',
      visible: true,
      transferVisible: true,
    },
    {
      value: 'payment_refund',
      viewValue: 'Payment Refund',
      visible: true,
      paymentVisible: true,
    },
  ];
  transactionsBody: IStripeTransactionResponseBody[] = [];
  stripeFeeData: IStripeFeeResponseBody[] = [];

  private tabMappings: Record<string, string> = {
    payments: 'payment',
    payouts: 'payout',
    'all transactions': 'All',
    'stripe fee': 'stripe fee',
    transfer_refund: 'transfer_refund',
    transfer: 'transfer',
    payment_refund: 'payment_refund',
    // Add other mappings as needed
    // Example: 'refunds': 'refund'
  };

  constructor(
    private transactionsService: TransactionsService,
    private fb: FormBuilder,
    private _snackbar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private dateConvertor: DateConverterService,
  ) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentDate = new Date().getDate();

    // this.minDate = new Date(2022, 9, 93);
    this.minDate = new Date(2025, 0, 1);
    this.maxDate = new Date(currentYear, currentMonth, currentDate);
  }

  ngOnInit(): void {
    this.initializeDefaultStripeDates();
    this.createStripeForm();
    this.getStripeData(this.selectedTab);
    // this.getStripeFeeData();
  }

  initializeDefaultStripeDates() {
    this.startDate = new Date(new Date().getFullYear(), 0, 1); // Start: Jan 1st of current year
    this.endDate = new Date(); // End: today
  }

  createStripeForm() {
    this.stripeDataForm = this.fb.group({
      startDate: [this.startDate, Validators.required],
      endDate: [this.endDate, Validators.required],
      filterType: [this.typesOfPayments[0].value],
    });
  }

  openDatePicker(): void {
    this.stripeDatePicker.open();
  }

  //on Date Change
  onDateRangeChange() {
    this.resetData();
    const startDate = this.stripeDataForm.controls['startDate'].value;
    const endDate = this.stripeDataForm.controls['endDate'].value;
    this.resetData();
    if (!startDate || !endDate) return;
    // this.currentOffset = 0;
    // // this.isFetching = false;
    if (
      (this.maxDate !== null && this.minDate !== null) ||
      this.maxDate > this.minDate
      // this.companyId !== null &&
      // this.selectedCategoryIds !== null)
    ) {
      if (
        this.minDate.getTime() != startDate &&
        this.maxDate.getTime() != endDate
      ) {
        this.startDate = startDate;
        this.endDate = endDate;
        this.getStripeData(this.selectedTab);
        if (this.selectedTab == 'stripe fee') this.getStripeFeeData();
      } else {
        this.startDate = this.minDate;
        this.endDate = this.maxDate;
        this.getStripeData(this.selectedTab);
        if (this.selectedTab == 'stripe fee') this.getStripeFeeData();
      }
    }
  }

  resetData() {
    this.pageOffset = 0;
    this.pageIndex = 0;
    // this.paginator.pageIndex = 0;
    this.transactionsBody.length = 0;
    // this.resultsLen = 0;
    this.transactionsBody = [];
  }

  getStripeData(selectedTab: string): void {
    const body: IStripeTransactionRequest = {
      startDate: this.startDate,
      endDate: this.endDate,
      ascending: this.currentSortDirection === 'asc' ? true : false,
      columnName: this.currentSortColumn,
      pageOffset: this.pageOffset,
      pageSize: this.pageSize,
      paymentType: selectedTab,
      sortOrder: this.currentSortDirection, // Use selected tab to determine payment type
    };
    this.transactionsService.getStripeData(body).subscribe({
      next: (data: IStripeTransactionResponse) => {
        if (!data.isError) {
          this.transactionsBody = data.value.stripeTransDetails;

          this.filterAndDisplayTransactions(selectedTab);
          this.cdr.detectChanges();
          this.totalResults = data.value.stripeTransDetailsCount; //this.transactionsBody.length //|| data.value.totalCount;
          this.currentSortColumn = this.currentSortColumn;
          this.currentSortDirection = this.currentSortDirection;
        }
      },
      error: (error) => {
        this._snackbar.open(error, 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  getStripeFeeData(): void {
    const body: IStripeTransactionRequest = {
      startDate: this.startDate,
      endDate: this.endDate,
      ascending: this.currentSortDirection === 'asc' ? true : false,
      columnName: this.currentSortColumn ? this.currentSortColumn : 'Id',
      pageOffset: this.pageOffset,
      pageSize: this.pageSize,
      paymentType: this.selectedTab,
      sortOrder: this.currentSortDirection,
    };
    this.transactionsService.getStripeFeeData(body).subscribe({
      next: (data: IStripeFeeResponse) => {
        if (!data.isError) {
          this.stripeFeeData = data.value.stripeFeeTransDetails;
          this.totalResults = data.value.stripeFeeCount;
          this.stripeFeeData.forEach((stripeItem: any) => {
            stripeItem.createdDate = stripeItem.createdDate.split(',')[0];
            stripeItem.modifiedDate = stripeItem.modifiedDate.split(',')[0];
          });
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this._snackbar.open(error, 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  /**
   * Handles page change events from the PaginationComponent.
   * @param page The new page number.
   */
  onPageChange(page: any): void {
    this.displayedTransactions = [];
    this.stripeFeeData = [];
    this.pageSize = page.pageSize; // Update page size if needed
    this.pageOffset = page.pageIndex * this.pageSize; // Calculate page offset for API calls
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.displayedTransactions = this.displayedTransactions.slice(
      startIndex,
      endIndex,
    );
    if (this.selectedTab == 'stripe fee') this.getStripeFeeData();
    else this.getStripeData(this.selectedTab);
    this.cdr.detectChanges();
  }

  /**
   * Handles sort events from the TransactionsTableComponent.
   * @param sortEvent An object containing the column and direction to sort by.
   */
  onSort(sort: any): void {
    if (!sort.column || sort.direction === '') {
      this.transactions = this.transactions;
      return;
    }

    this.transactions = this.transactions.sort((a: any, b: any) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'id':
          return this.compare(a.id, b.id, isAsc);
        case 'creationDate':
          return this.compare(a.creationDate, b.creationDate, isAsc);
        default:
          return 0;
      }
    });
    this.currentSortColumn = sort.column;
    this.currentSortDirection = sort.direction;
    if (this.selectedTab == 'stripe fee') this.getStripeFeeData();
    else this.getStripeData(this.selectedTab);
  }

  compare(a: number | string, b: number | string, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  filterAndDisplayTransactions(tabLabel: string): void {
    let filteredTransactions: IStripeTransactionResponseBody[] = [];
    const filteredStripeFeeTransactions: IStripeFeeResponseBody[] = [];
    let columns: Column[] = [];

    switch (tabLabel) {
      case 'payment':
        filteredTransactions = this.transactionsBody.filter(
          (transaction) => transaction.transactionType === 'payment',
        );
        columns = this.paymentColumns;
        this.resetData();
        break;
      case 'payout':
        filteredTransactions = this.transactionsBody.filter(
          (transaction) => transaction.transactionType === 'payout',
        );
        columns = this.payoutsColumns;
        this.resetData();
        break;
      case 'transfer':
        filteredTransactions = this.transactionsBody.filter(
          (transaction) => transaction.transactionType === 'transfer',
        );
        columns = this.transferColumns;
        this.resetData();
        break;
      case 'All':
        filteredTransactions = this.transactionsBody;
        columns = this.allTransactionColumns;
        this.cdr.detectChanges();
        // this.resetData();
        break;
      // case 'stripe fee':
      //   columns = this.stripeFeeColumns;
      //   break;
      case 'transfer_refund':
        filteredTransactions = this.transactionsBody.filter(
          (transaction) => transaction.transactionType === 'transfer_refund',
        );
        columns = this.paymentColumns;
        break;
      case 'payment_refund':
        filteredTransactions = this.transactionsBody.filter(
          (transaction) => transaction.transactionType === 'payment_refund',
        );
        columns = this.paymentColumns;
        break;
      default:
        filteredTransactions = this.transactionsBody.filter(
          (transaction) => transaction.transactionType === 'payment',
        );
        columns = this.paymentColumns;
        break;
    }

    this.displayedTransactions = filteredTransactions;
    this.displayedColumns = columns;
  }

  paymentTypeChange(event: string) {
    this.filterAndDisplayTransactions(event);
  }

  /*
   * @param event The MatTabChangeEvent object.
   */
  onTabChange(event: MatTabChangeEvent): void {
    const tabLabel = event.tab.textLabel.toLowerCase();
    this.isTypeOfPayment =
      tabLabel === 'payments' || tabLabel === 'transfer' ? true : false;
    this.selectedTab = this.tabMappings[tabLabel] || tabLabel;
    this.pageIndex = 1;
    if (tabLabel === 'stripe fee') {
      this.getStripeFeeData();
    } else {
      this.getStripeData(this.selectedTab);
    }
    this.cdr.detectChanges();
  }
}
